import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { Button } from '@/components/ui/button';
import { ReviewStatusBadge, ReviewBadgeStatus } from '@/components/ui/ReviewStatusBadge';
import { TrainingVideo } from '@/types/videoAnnotations';
import { useI18n } from '@/contexts/I18nContext';
import { REVIEW_COLOR_PENDING, REVIEW_COLOR_IN_REVIEW } from '@/styles/brand';
import {
  InstructorReviewStatus,
  getInstructorStatus,
} from '../hooks/useInstructorReviews';
import { acceptanceDeadline, deliveryDeadline } from '../utils/reviewWindows';
import { useCountdown } from '../hooks/useCountdown';

// ── Status badge label map ────────────────────────────────────────────────

const statusBadgeStatus: Record<InstructorReviewStatus, ReviewBadgeStatus> = {
  pending:   'pending',
  in_review: 'in_review',
  delivered: 'delivered',
  declined:  'declined',
  cancelled: 'cancelled',
  expired:   'expired',
};

// ── Main component ────────────────────────────────────────────────────────

interface InstructorReviewCardProps {
  video: TrainingVideo;
  onAccept: (id: string) => void;
  onDecline: (id: string, practitionerId?: string) => void;
  isAccepting: boolean;
  isDeclining: boolean;
  isAtLimit?: boolean;
}

export const InstructorReviewCard: React.FC<InstructorReviewCardProps> = ({
  video,
  onAccept,
  onDecline,
  isAccepting,
  isDeclining,
  isAtLimit = false,
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [confirmingDecline, setConfirmingDecline] = useState(false);

  const status = getInstructorStatus(video);

  // Practitioner info (the person who submitted the roll)
  const practitioner = video.uploader;
  const name = practitioner?.full_name ?? t('manage_reviews.unknown_practitioner');
  const initials = name
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const beltRank = practitioner?.belt_rank ?? 'white';
  const submittedDate = format(new Date(video.created_at), 'MMM d, yyyy');

  const excerpt = video.description
    ? `"${video.description.length > 60 ? video.description.slice(0, 60) + '…' : video.description}"`
    : null;

  const statusLabel: Record<InstructorReviewStatus, string> = {
    pending:   t('my_reviews.status_pending'),
    in_review: t('my_reviews.status_in_review'),
    delivered: t('my_reviews.status_delivered'),
    declined:  t('manage_reviews.status_declined'),
    cancelled: t('manage_reviews.status_cancelled'),
    expired:   t('my_reviews.status_expired'),
  };

  // ── Countdown for pending cards (48 h acceptance window) ─────────────────
  const AcceptCountdown: React.FC = () => {
    const deadline = acceptanceDeadline(video);
    const { isOverdue, hours, minutes } = useCountdown(deadline);

    if (isOverdue) {
      return <span className="text-white/30 text-xs">{t('manage_reviews.expired_window')}</span>;
    }

    return (
      <span className="text-xs" style={{ color: REVIEW_COLOR_PENDING }}>
        {t('manage_reviews.time_to_accept', { h: hours, m: minutes })}
      </span>
    );
  };

  // ── Countdown for in_review cards (72 h delivery window) ─────────────────
  const DeliveryCountdown: React.FC = () => {
    const deadline = deliveryDeadline(video);
    const { isOverdue, hours, minutes } = useCountdown(deadline);

    if (isOverdue) {
      return (
        <span className="text-red-400 text-xs">
          {t('manage_reviews.overdue', { h: hours })}
        </span>
      );
    }

    return (
      <span className="text-xs" style={{ color: REVIEW_COLOR_IN_REVIEW }}>
        {t('manage_reviews.time_to_deliver', { h: hours, m: minutes })}
      </span>
    );
  };

  // ── Card click (non-actionable area) ─────────────────────────────────────
  const handleCardClick = () => {
    if (status === 'in_review' || status === 'delivered') {
      navigate(`/video-review/${video.id}`);
    }
  };

  // ── Decline flow ──────────────────────────────────────────────────────────
  const handleDeclineClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmingDecline(true);
  };

  const handleConfirmDecline = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDecline(video.id, video.uploader_id);
    setConfirmingDecline(false);
  };

  const handleCancelDecline = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmingDecline(false);
  };

  const handleAcceptClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAccept(video.id);
  };

  // Left border color per status
  const leftBorderColor =
    status === 'pending'   ? REVIEW_COLOR_PENDING :
    status === 'in_review' ? REVIEW_COLOR_IN_REVIEW :
    undefined;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      data-testid="review-card"
      onClick={handleCardClick}
      className={[
        'w-full text-left bg-sossiety-surface border border-white/5 rounded-2xl p-4 flex flex-col gap-3',
        leftBorderColor ? 'border-l-[3px]' : '',
        status === 'in_review' || status === 'delivered'
          ? 'hover:bg-sossiety-overlay active:bg-sossiety-overlay/80 transition-colors cursor-pointer'
          : '',
      ].join(' ')}
      style={leftBorderColor ? { borderLeftColor: leftBorderColor } : undefined}
    >
      {/* Top row: avatar + name + belt + date + status badge */}
      <div className="flex items-center gap-3">
        <FramedAvatar
          src={practitioner?.profile_picture_url}
          alt={name}
          initials={initials}
          size={36}
          frameSize={{ w: 48, h: 52 }}
          beltRank={beltRank}
          isManual
        />

        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-bold leading-tight truncate">{name}</p>
          <p className="text-white/40 text-xs mt-0.5">{submittedDate}</p>
        </div>

        <ReviewStatusBadge
          status={statusBadgeStatus[status]}
          label={statusLabel[status]}
        />
      </div>

      {/* Message excerpt — only shown when practitioner left a message */}
      {excerpt && (
        <p className="text-white/60 text-sm leading-snug line-clamp-2">{excerpt}</p>
      )}

      {/* Footer: contextual per status */}
      <CardFooter
        status={status}
        video={video}
        t={t}
        confirmingDecline={confirmingDecline}
        isAccepting={isAccepting}
        isDeclining={isDeclining}
        isAtLimit={isAtLimit}
        onAcceptClick={handleAcceptClick}
        onDeclineClick={handleDeclineClick}
        onConfirmDecline={handleConfirmDecline}
        onCancelDecline={handleCancelDecline}
        onContinueReviewClick={(e) => {
          // SOS-360: never deliver from the list — open the edit screen.
          // Delivery is an explicit action inside VideoReviewDetail.
          e.stopPropagation();
          navigate(`/video-review/${video.id}`);
        }}
        AcceptCountdown={AcceptCountdown}
        DeliveryCountdown={DeliveryCountdown}
      />
    </div>
  );
};

// ── Card footer per status ────────────────────────────────────────────────

interface FooterProps {
  status: InstructorReviewStatus;
  video: TrainingVideo;
  t: ReturnType<typeof useI18n>['t'];
  confirmingDecline: boolean;
  isAccepting: boolean;
  isDeclining: boolean;
  isAtLimit: boolean;
  onAcceptClick: (e: React.MouseEvent) => void;
  onDeclineClick: (e: React.MouseEvent) => void;
  onConfirmDecline: (e: React.MouseEvent) => void;
  onCancelDecline: (e: React.MouseEvent) => void;
  onContinueReviewClick: (e: React.MouseEvent) => void;
  AcceptCountdown: React.FC;
  DeliveryCountdown: React.FC;
}

const CardFooter: React.FC<FooterProps> = ({
  status,
  video,
  t,
  confirmingDecline,
  isAccepting,
  isDeclining,
  isAtLimit,
  onAcceptClick,
  onDeclineClick,
  onConfirmDecline,
  onCancelDecline,
  onContinueReviewClick,
  AcceptCountdown,
  DeliveryCountdown,
}) => {
  if (status === 'pending') {
    if (confirmingDecline) {
      return (
        <div data-testid="decline-confirm" className="flex items-center gap-2 pt-1">
          <span className="flex-1 text-xs text-white/60">{t('manage_reviews.confirm_decline')}</span>
          <Button
            variant="ghost"
            size="xs"
            onClick={onCancelDecline}
            className="border border-white/20 text-white/60 hover:border-white/40"
          >
            {t('manage_reviews.confirm_cancel')}
          </Button>
          <Button
            data-testid="decline-confirm-yes"
            variant="destructive"
            size="xs"
            onClick={onConfirmDecline}
            disabled={isDeclining}
          >
            {isDeclining ? '…' : t('manage_reviews.confirm_yes')}
          </Button>
        </div>
      );
    }

    // Limit reached — block accept with explanation
    if (isAtLimit) {
      return (
        <div className="flex items-start gap-2 pt-1 p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <span className="text-amber-400 text-xs leading-snug">
            {t('manage_reviews.limit_reached_desc')}
          </span>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between gap-2 pt-1">
        <div data-testid="countdown-timer">
          <AcceptCountdown />
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="decline-button"
            variant="ghost"
            size="xs"
            onClick={onDeclineClick}
            className="border border-white/20 text-white/50 hover:border-white/40"
          >
            {t('manage_reviews.decline')}
          </Button>
          <Button
            data-testid="accept-button"
            variant="primary"
            size="xs"
            onClick={onAcceptClick}
            disabled={isAccepting}
            className="px-4 flex items-center gap-1.5"
          >
            {isAccepting && <Loader2 size={12} className="animate-spin" />}
            {t('manage_reviews.accept')}
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'in_review') {
    return (
      <div className="flex flex-col gap-2 pt-1">
        <div className="flex items-center justify-between">
          <div data-testid="countdown-timer">
            <DeliveryCountdown />
          </div>
        </div>
        <Button
          data-testid="continue-review-button"
          variant="primary"
          size="xs"
          onClick={onContinueReviewClick}
          className="w-full flex items-center justify-center gap-1.5"
        >
          {t('manage_reviews.continue_review')}
        </Button>
      </div>
    );
  }

  if (status === 'delivered') {
    const deliveredDate = format(new Date(video.updated_at), 'MMM d, yyyy');
    return (
      <div className="flex items-center justify-between pt-1">
        <span className="text-white/40 text-xs">
          {t('manage_reviews.delivered_on', { date: deliveredDate })}
        </span>
        <span className="text-xs font-semibold text-green-400">
          {t('manage_reviews.view_review')}
        </span>
      </div>
    );
  }

  if (status === 'declined') {
    return (
      <div className="pt-1">
        <span className="text-white/30 text-xs">{t('manage_reviews.you_declined')}</span>
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div className="pt-1">
        <span className="text-white/30 text-xs">{t('manage_reviews.practitioner_cancelled')}</span>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="pt-1">
        <span className="text-white/30 text-xs">{t('manage_reviews.expired_window')}</span>
      </div>
    );
  }

  return null;
};
